import { AnimatePresence, motion } from "framer-motion";
import { Filter } from "lucide-react";
import React from "react";

import { cn } from "@/lib/utils";

interface FilterDrawerPanelProps {
  activeCount: number;
  showFilters: boolean;
  onToggleFilters: () => void;
  onClearAll?: () => void;
  rightSlot?: React.ReactNode;
  chips?: React.ReactNode;
  drawerContent?: React.ReactNode;
  label?: string;
  headerClassName?: string;
  activeButtonClassName?: string;
  inactiveButtonClassName?: string;
  countClassName?: string;
  clearButtonClassName?: string;
  chipsWrapperClassName?: string;
  chipsInnerClassName?: string;
  drawerWrapperClassName?: string;
  drawerInnerClassName?: string;
}

export function FilterDrawerPanel({
  activeCount,
  showFilters,
  onToggleFilters,
  onClearAll,
  rightSlot,
  chips,
  drawerContent,
  label = "Filters",
  headerClassName,
  activeButtonClassName,
  inactiveButtonClassName,
  countClassName,
  clearButtonClassName,
  chipsWrapperClassName,
  chipsInnerClassName,
  drawerWrapperClassName,
  drawerInnerClassName,
}: FilterDrawerPanelProps) {
  const hasActiveFilters = activeCount > 0;

  return (
    <>
      <div
        className={cn(
          "flex h-10 shrink-0 items-center justify-between gap-2 border-b border-border/40 px-4 py-2",
          headerClassName,
        )}
      >
        <button
          type="button"
          onClick={onToggleFilters}
          aria-expanded={showFilters}
          className={cn(
            "inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md border px-2.5 text-[11.5px] font-medium transition-colors cursor-pointer outline-none",
            hasActiveFilters || showFilters
              ? "border-transparent bg-muted text-foreground"
              : "border-border bg-background hover:bg-muted/50 text-muted-foreground hover:text-foreground",
            hasActiveFilters || showFilters
              ? activeButtonClassName
              : inactiveButtonClassName,
          )}
        >
          <Filter className="size-3 shrink-0" />
          {label}
          {hasActiveFilters ? (
            <span
              className={cn(
                "ml-1 flex min-w-4 items-center justify-center rounded-full bg-background px-1.5 py-0.5 text-[10px] font-semibold leading-none text-foreground",
                countClassName,
              )}
            >
              {activeCount}
            </span>
          ) : null}
        </button>

        <div className="ml-auto flex shrink-0 items-center gap-2">
          {rightSlot}
          {hasActiveFilters && onClearAll ? (
            <button
              type="button"
              onClick={onClearAll}
              className={cn(
                "text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground cursor-pointer",
                clearButtonClassName,
              )}
            >
              Clear all
            </button>
          ) : null}
        </div>
      </div>

      <AnimatePresence initial={false}>
        {hasActiveFilters && chips ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15, ease: "easeInOut" }}
            className={cn(
              "overflow-hidden shrink-0 border-b border-border/40 bg-muted/10",
              chipsWrapperClassName,
            )}
          >
            <div
              className={cn(
                "flex flex-wrap items-center gap-1 px-3 py-1.5 text-xs",
                chipsInnerClassName,
              )}
            >
              {chips}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {showFilters && drawerContent ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className={cn(
              "overflow-hidden shrink-0 border-b border-border/40 bg-muted/20 flex flex-col",
              drawerWrapperClassName,
            )}
          >
            <div
              className={cn(
                "space-y-2 px-3 py-2 flex-1 min-h-0 overflow-y-auto",
                drawerInnerClassName,
              )}
            >
              {drawerContent}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  );
}
